import { activeSiteConfig } from "@config/site/site-config";
import {
    MAP_TAB_SWITCH_LIST,
    MAP_TAB_SWITCH_MAP,
} from "@e2e/helpers/store-locator/constants/common";
import type { LineItem } from "@graphql/generated/types";
import { CommonCMS } from "@lib/constants/contentful";
import ClickAndCollect from "@lib/constants/contentful/clickAndCollect";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Button, ButtonGroup } from "@ui/components/forms/button/Button";
import { AspectRatio } from "@ui/components/layout/aspect-ratio/AspectRatio";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Grid, GridItem } from "@ui/components/layout/grid/Grid";
import { HStack } from "@ui/components/layout/stack/Stack";
import { scrollbarStyles } from "@ui/css/01.scrollbar/scrollbar.styles";
import { useState } from "react";
import { Capture } from "./capture";
import { CardItem } from "./CardItem";
import { CardItems } from "./CardItems";
import { Provider, type StoreData } from "./controller";
import { FilterDropdowns } from "./FilterDropdowns";
import { filterValuesDistance, filterValueStoreType } from "./filterValues";
import { Search } from "./search";
import { StoreCount } from "./StoreCount";
import { StoreList } from "./StoreList";
import { StoreLocatorMap } from "./StoreLocatorMap";
import { StoreModal } from "./StoreModal";
import { ShippingMethodTypes } from "@components/commerce/checkout/types";
import type { ProductVariant } from "@lib/product/types";
import { use2xlBreakpoint, useLgBreakpoint, useXlBreakpoint } from "@ui/hooks/useBreakpoint";

interface StoreLocatorProps {
    storesWithStock?: StoreData[];
    selectedProductData?: any | LineItem | LineItem[];
    selectedVariant?: ProductVariant;
    onClose?: () => void;
    shippingMethodKey?: ShippingMethodTypes;
    isPickUpSelection?: boolean;
}

export const StoreLocator = ({
    storesWithStock,
    selectedProductData,
    onClose,
    selectedVariant,
    shippingMethodKey,
    isPickUpSelection = true,
}: StoreLocatorProps) => {
    const { getMultiple } = useMicrocopy();

    const isLargeViewport = useLgBreakpoint();
    const isExtraLargeViewport = useXlBreakpoint();
    const isTwoExtraLargeViewport = use2xlBreakpoint();

    // If we have a selectedVariant, that means will be StoreLocator for C&C.
    // The default behavior of the StoreLocator is to show the list view, on C&C, otherwise show the map.
    const [tab, setTab] = useState<"list" | "map">("map");

    const microcopies = getMultiple(CommonCMS.page, [
        ClickAndCollect.storeLocatorList,
        ClickAndCollect.storeLocatorMap,
        ClickAndCollect.storesWithin,
    ]);

    const isMapTab = tab === "map";
    const { country } = activeSiteConfig;

    const hideCaptureButton =
        activeSiteConfig.checkout?.parcelShops?.searchMethod === "address" &&
        shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP;

    return (
        <Provider
            initStores={storesWithStock as unknown as StoreData[]}
            shippingMethodKey={shippingMethodKey}
        >
            <Grid
                templateColumns={[
                    `repeat(4, 1fr)`,
                    `repeat(4, 1fr)`,
                    `repeat(4, 1fr)`,
                    `repeat(12, 1fr)`,
                ]}
                flex={1}
                h={{ base: isMapTab ? "full" : "calc(100vh - 65px)" }}
            >
                <GridItem
                    gridRow={[1]}
                    colStart={[1]}
                    colSpan={{ base: 4 }}
                    sx={{
                        ...(isExtraLargeViewport && { contain: "size" }),
                        overflow: "hidden",
                    }}
                >
                    <Flex
                        direction="column"
                        align="stretch"
                        maxHeight="100%"
                        pr={{ base: 0, lg: 3 }}
                        h={[
                            // TODO: Refactor this to make adapt all the mediaqueries in a responsive way.
                            "full",
                        ]}
                    >
                        {isPickUpSelection &&
                            (selectedProductData && selectedProductData?.length >= 1 ? (
                                <Box mb={6}>
                                    <CardItems selectedProductData={selectedProductData} />
                                </Box>
                            ) : (
                                <Box mb={6}>
                                    <CardItem selectedProductData={selectedProductData} />
                                </Box>
                            ))}
                        <Search isPickUpSelection={isPickUpSelection} />
                        {!hideCaptureButton && (
                            <HStack justifyContent="space-between" pt={3} pb={4}>
                                <Capture shippingMethodKey={shippingMethodKey} />
                            </HStack>
                        )}
                        {!isPickUpSelection && (
                            <>
                                <Flex mx={[3, 0]} gap={"0.2"} mb={4}>
                                    <FilterDropdowns
                                        // If the country is US or JP, we show the US/JP filter with different values, otherwise we show the CA/Default filter values with km/english
                                        values={
                                            filterValuesDistance[
                                                country === "US" || country === "JP"
                                                    ? country
                                                    : "Default"
                                            ]
                                        }
                                        filterType={"distance"}
                                    />
                                    <FilterDropdowns
                                        values={filterValueStoreType}
                                        filterType={"storeType"}
                                    />
                                </Flex>
                                {isExtraLargeViewport && (
                                    <StoreCount
                                        microcopy={microcopies[ClickAndCollect.storesWithin]}
                                    />
                                )}
                            </>
                        )}
                        <Flex alignItems={"center"} mb={4} justifyContent={"space-between"}>
                            {!isLargeViewport && (
                                <>
                                    <StoreCount
                                        isExtraLargeViewport={isExtraLargeViewport}
                                        microcopy={microcopies[ClickAndCollect.storesWithin]}
                                    />
                                    <ButtonGroup spacing="px" ml={"auto"}>
                                        <Button
                                            size="xs"
                                            variant={tab === "list" ? "solid" : "tertiary"}
                                            onClick={() => setTab("list")}
                                            fontSize="mobileLinkL1"
                                            data-testid={MAP_TAB_SWITCH_LIST}
                                        >
                                            <span>
                                                {microcopies[ClickAndCollect.storeLocatorList]}
                                            </span>
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant={tab === "map" ? "solid" : "tertiary"}
                                            onClick={() => setTab("map")}
                                            fontSize="mobileLinkL1"
                                            data-testid={MAP_TAB_SWITCH_MAP}
                                        >
                                            <span>
                                                {microcopies[ClickAndCollect.storeLocatorMap]}
                                            </span>
                                        </Button>
                                    </ButtonGroup>
                                </>
                            )}
                        </Flex>
                        <Flex flex={1} direction="column" overflow="hidden" display="flex">
                            <Box
                                flex={1}
                                overflow="auto"
                                sx={{
                                    div: {
                                        ...scrollbarStyles,
                                    },
                                }}
                                h="full"
                            >
                                <StoreList
                                    selectedVariant={selectedVariant}
                                    onClose={onClose}
                                    hideLoader={!isExtraLargeViewport && isMapTab}
                                    storesWithStock={
                                        !isExtraLargeViewport ? storesWithStock : undefined
                                    }
                                    shippingMethodKey={shippingMethodKey}
                                />
                            </Box>
                        </Flex>
                    </Flex>
                </GridItem>
                <GridItem
                    colStart={isMapTab ? [] : [1]}
                    gridRow={isMapTab ? [] : [1]}
                    colSpan={{ base: 4, lg: 8 }}
                >
                    {!isExtraLargeViewport && (
                        <AspectRatio ratio="2/3" left={isMapTab ? 0 : "-200%"}>
                            <StoreLocatorMap
                                // We are using this value only to determine the zoom levels for the map
                                isExtraLargeViewport={isTwoExtraLargeViewport}
                                storesWithStock={storesWithStock}
                                shippingMethodKey={shippingMethodKey}
                            />
                        </AspectRatio>
                    )}
                    {isExtraLargeViewport && (
                        // We need to calculate the total height of the viewport - the height of the header and the navigation - the space below the map and footer
                        <Box minHeight={"calc(100vh - 120px)"} position="relative">
                            <StoreLocatorMap
                                isExtraLargeViewport={isTwoExtraLargeViewport}
                                storesWithStock={storesWithStock}
                                shippingMethodKey={shippingMethodKey}
                            />
                        </Box>
                    )}
                </GridItem>
                <StoreModal
                    onClose={onClose}
                    selectedVariant={selectedVariant}
                    shippingMethodKey={shippingMethodKey}
                />
            </Grid>
        </Provider>
    );
};
